import { useState } from "react";
import { Col, Row, Select, DatePicker, Spin } from "antd";
import {
  BarChartOutlined,
  LineChartOutlined,
  TableOutlined,
  PieChartOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import moment from "moment";

import DashboardPanel from "./components/DashboardPanel";
import InsightBox from "./components/InsightBox";
import InboundCapacityChart from "./components/InboundCapacityChart";
import ImportExportChart from "./components/ImportExportChart";
import ClearanceTable from "./components/ClearanceTable";
import ShedOccupationChart from "./components/ShedOccupationChart";
import { useFetch } from "../../../../../hooks/useFetch";

import MyPage from "../../../../../components/pages/Page";

const { RangePicker } = DatePicker;

const fmtDT = (v) => {
  if (!v) return "-";
  const m = moment(v);
  return m.isValid() ? m.format("DD/MM/YYYY HH:mm") : String(v);
};

export default () => {

  // --- 1. Grafik Kapasitas & Pergerakan: live dari API ---
  // GET /api/v1/beacukai/dashboard/goods_movement?start_date=YYYYMMDD&end_date=YYYYMMDD&capacity=100
  // start_date & end_date dari Filter Tampilan (default 7 hari terakhir)
  // respons (payload.data): { capacity, labels, inbound[], count[],
  //   capacity_line[], data: [{hour|date, bruto, count, capacity}], analysis, ... }
  // default tanggal = hari ini ("sekarang")
  // default range filter = 7 hari terakhir (start s.d. end)
  const [gmStartDate, setGmStartDate] = useState(() =>
    moment().subtract(6, "days").startOf("day")
  );
  const [gmDate, setGmDate] = useState(() => moment());
  const [gmCapacity] = useState(300);
  const gmQuery = `api/v1/beacukai/dashboard/goods_movement?start_date=${gmStartDate.format(
    "YYYYMMDD"
  )}&end_date=${gmDate.format("YYYYMMDD")}&capacity=${gmCapacity}`;
  const [gmResp, gmLoading] = useFetch(gmQuery);

  const gmRows = Array.isArray(gmResp?.data) ? gmResp.data : [];
  // mapping respons API -> format chart { time, inbound, count, capacity }
  // murni dari API: kosong saat loading / tidak ada data (tanpa dummy)
  const inboundChartData = gmRows.map((d) => ({
    time: d.hour ?? d.date,
    inbound: Number(d.bruto ?? d.inbound) || 0,
    count: d.count,
    capacity: d.capacity,
  }));
  const inboundCapacityVal = gmResp?.capacity ?? gmCapacity;
  const gmAnalysis = gmResp?.analysis || "";

  // --- 2. Grafik Impor & Ekspor: live dari API ---
  // GET /api/v1/beacukai/dashboard/eks_imp?start_date=YYYYMMDD&end_date=YYYYMMDD
  // start_date & end_date dari Filter Tampilan (default 7 hari terakhir)
  // respons (payload.data): { data: [{date, impor, ekspor}],
  //   total_impor, total_ekspor, impor_change_pct, ekspor_change_pct, summary[] }
  const eksImpQuery = `api/v1/beacukai/dashboard/eks_imp?start_date=${gmStartDate.format(
    "YYYYMMDD"
  )}&end_date=${gmDate.format("YYYYMMDD")}`;
  const [eksImpResp, eksImpLoading] = useFetch(eksImpQuery);

  const eksImpRows = Array.isArray(eksImpResp?.data) ? eksImpResp.data : [];
  // mapping respons API -> format chart { period, importValue, exportValue }
  // murni dari API: kosong saat loading / tidak ada data (tanpa dummy)
  const tradeChartData = eksImpRows.map((d) => ({
    period: String(d.date),
    importValue: Number(d.impor) || 0,
    exportValue: Number(d.ekspor) || 0,
  }));
  const eksImpSummary = Array.isArray(eksImpResp?.summary)
    ? eksImpResp.summary
    : null;

  // --- 3. Tabel Import Clearance Time: live dari API ---
  // GET /api/v1/beacukai/dashboard/clearance_time?start_date=YYYYMMDD&end_date=YYYYMMDD
  // start_date & end_date dari Filter Tampilan (default 7 hari terakhir)
  // respons (payload.data): { data: [{ no_bl_awb, consignee, waktu_tiba,
  //   waktu_keluar, clearance_hours, clearance_time, status }], analysis }
  // status: "normal" | "warning" | "danger"
  const ctQuery = `api/v1/beacukai/dashboard/clearance_time?start_date=${gmStartDate.format(
    "YYYYMMDD"
  )}&end_date=${gmDate.format("YYYYMMDD")}`;
  const [ctResp, ctLoading] = useFetch(ctQuery);
  const ctApiRows = Array.isArray(ctResp?.data) ? ctResp.data : [];
  // mapping respons API -> format ClearanceTable
  const ctAnalysis = ctResp?.analysis || "";
  const clearanceTableData = ctApiRows.map((d, i) => ({
    key: `${d.no_bl_awb}-${i}`,
    no: i + 1,
    blAwb: d.no_bl_awb ?? "-",
    consignee: d.consignee ?? "-",
    arrival: fmtDT(d.waktu_tiba),
    gateOut: fmtDT(d.waktu_keluar),
    clearance: d.clearance_time ?? "-",
    status: d.status ?? "-",
    clearanceHours: Number(d.clearance_hours) || 0,
  }));

  // --- 4. Grafik Shed Occupation Ratio (SoR): live dari API ---
  // GET /api/v1/beacukai/dashboard/occupation_ratio?start_date=YYYYMMDD&end_date=YYYYMMDD&capacity=300
  // start_date & end_date dari Filter Tampilan, capacity SoR = 300
  // respons (payload.data): { start_date, end_date, capacity, occupied,
  //   available, occupation_pct, over_capacity, analysis }
  const sorQuery = `api/v1/beacukai/dashboard/occupation_ratio?start_date=${gmStartDate.format(
    "YYYYMMDD"
  )}&end_date=${gmDate.format("YYYYMMDD")}&capacity=300`;
  const [sorResp, sorLoading] = useFetch(sorQuery);
  const sorOccupied = Number(sorResp?.occupation_pct) || 0;
  const sorAnalysis = sorResp?.analysis || "";

  return (
    <MyPage title={"Dasboard situasi TPS"}>
      <div
        className="dashboard-filters"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "20px",
          paddingBottom: "4px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "13px", color: "#1e293b", fontWeight: 500 }}>
            Filter Tampilan:
          </span>

          <Select
            defaultValue="harian"
            style={{ width: 100 }}
            size="small"
            options={[
              { value: "harian", label: "Harian" },
              { value: "mingguan", label: "Mingguan" },
            ]}
          />

          <Select
            defaultValue="bulanan"
            style={{ width: 105 }}
            size="small"
            options={[
              { value: "bulanan", label: "Bulanan" },
              { value: "tahunan", label: "Tahunan" },
            ]}
          />

          <RangePicker
            size="small"
            style={{ width: 350, flexShrink: 0 }}
            prefixIcon={
              <CalendarOutlined
                style={{ color: "#64748b", fontSize: "12px" }}
              />
            }
            suffixIcon={
              <CalendarOutlined
                style={{ color: "#64748b", fontSize: "12px" }}
              />
            }
            separator=" - "
            value={[gmStartDate, gmDate]}
            onChange={(dates) => {
              // start & end mengendalikan tampilan range;
              // query API (goods_movement / eks_imp) pakai tanggal akhir
              if (dates?.[0]) setGmStartDate(dates[0].clone());
              const end = dates?.[1] || dates?.[0];
              if (end) setGmDate(end.clone());
            }}
            format="DD MMMM YYYY"
          />
        </div>

        <div style={{ fontSize: "12px", color: "#475569" }}>
          Terakhir Diperbarui:{" "}
          <span style={{ fontWeight: 500 }}>
            {gmResp?.date
              ? moment(gmResp.date, "YYYYMMDD").format("DD MMMM YYYY")
              : gmDate.format("DD MMMM YYYY")}
            {gmLoading ? " (memuat...)" : ""}
          </span>
        </div>
      </div>

      <Row gutter={[12, 12]} align="stretch">
        <Col xs={24} lg={12}>
          <DashboardPanel
            title="1. Grafik Kapasitas & Jumlah Pergerakan Barang di Gudang"
            icon={<LineChartOutlined />}
          >
            <Spin spinning={gmLoading}>
              <InboundCapacityChart
                data={inboundChartData}
                capacity={inboundCapacityVal}
                height={220}
              />
            </Spin>
            <InsightBox>
              <strong>Analisa Singkat (Otomatis):</strong>{" "}
              {gmAnalysis ||
                (gmLoading
                  ? "Memuat analisa..."
                  : "Belum ada analisa untuk periode ini.")}
            </InsightBox>
          </DashboardPanel>
        </Col>

        <Col xs={24} lg={12}>
          <DashboardPanel
            title="2. Analisa Grafik Jumlah Impor dan Ekspor"
            icon={<BarChartOutlined />}
          >
            <Spin spinning={eksImpLoading}>
              <ImportExportChart data={tradeChartData} height={220} />
            </Spin>
            <InsightBox>
              <strong>Ringkasan Analisa (Otomatis):</strong>
              {eksImpSummary ? (
                <ul>
                  {eksImpSummary.map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              ) : (
                <ul>
                  <li>
                    {eksImpLoading
                      ? "Memuat ringkasan..."
                      : "Belum ada ringkasan untuk periode ini."}
                  </li>
                </ul>
              )}
            </InsightBox>
          </DashboardPanel>
        </Col>

        <Col xs={24} lg={16}>
          <DashboardPanel
            title="3. Analisa Tabel Import Clearance Time (Waktu Tunggu Impor)"
            icon={<TableOutlined />}
          >
            <Spin spinning={ctLoading}>
              <ClearanceTable data={clearanceTableData} />
            </Spin>
            <InsightBox className="table-insight">
              {ctLoading
                ? "Memuat rata-rata clearance time..."
                : ctAnalysis ||
                  "Belum ada data clearance time untuk periode ini."}
            </InsightBox>
          </DashboardPanel>
        </Col>

        <Col xs={24} lg={8}>
          <DashboardPanel
            title="4. Grafik Shed Occupation Ratio (SoR)"
            icon={<PieChartOutlined />}
          >
            <Spin spinning={sorLoading}>
              <ShedOccupationChart
                occupied={sorOccupied}
                overCapacity={!!sorResp?.over_capacity}
              />
            </Spin>
            <InsightBox className="sor-insight">
              {sorLoading
                ? "Memuat analisa okupansi..."
                : sorAnalysis ||
                  "Belum ada data okupansi untuk periode ini."}
            </InsightBox>
          </DashboardPanel>
        </Col>
      </Row>
    </MyPage>
  );
};
